import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import debounce from 'lodash.debounce';
import { Box, Typography, CircularProgress } from '@mui/material';
import { Modal, Dropdown, Tabs, Button } from '../../components/Common';
import { PageHeader } from '../../components/Layout';
import { AddCategoryForm, EditCategoryForm, AddExperienceForm } from '../../components/Forms';
import { EditExperienceForm } from '../../components/Forms/Experiences/EditExperienceForm';
import { useCategories, DisplayCategory as Category } from './useCategories';
import { CategoriesTable } from '../../components/Tables/CategoriesTable';
import { ExperiencesTable } from '../../components/Tables';
import { toast } from 'react-toastify';

const Categories: React.FC = () => {
  const navigate = useNavigate();
  // Check authentication
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      toast.error('Authentication required. Please log in.');
      window.location.href = '/auth';
    } else {
      setIsAuthenticated(true);
    }
  }, []);
  const [activeTab, setActiveTab] = useState<'categories' | 'experiences'>('categories');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [isAddExperienceModalOpen, setIsAddExperienceModalOpen] = useState(false);
  const [isEditExperienceModalOpen, setIsEditExperienceModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<string | null>(null);
  const [selectedExperience, setSelectedExperience] = useState<{
    id: string;
    title: string;
    description: string;
    imageUrl?: string;
    locale?: import('../../types/category.types').OccasionLocale;
  } | null>(null);
  const [experienceRefresh, setExperienceRefresh] = useState(0);

  // Fetch categories from API
  const { 
    categories: apiCategories, 
    loading, 
    reorderCategories: apiReorderCategories, 
    deleteCategory: apiDeleteCategory,
    refetch: refetchCategories
  } = useCategories();
  const [localCategories, setLocalCategories] = useState<Category[]>(apiCategories);

  // Update local categories when API data changes
  React.useEffect(() => {
    setLocalCategories(apiCategories);
    console.log('📦 Categories updated:', apiCategories.length, 'categories');
  }, [apiCategories]);

  const [searchQuery, setSearchQuery] = useState('');

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItem(null);
  };

  const handleEditCategory = (category: Category) => {
    if (!isAuthenticated) {
      toast.error('Authentication required to edit categories');
      return;
    }
    setSelectedCategory(category);
    setIsEditMode(true);
    setIsCategoryModalOpen(true);
  };

  const handleDeleteClick = (categoryId: string) => {
    if (!isAuthenticated) {
      toast.error('Authentication required to delete categories');
      return;
    }
    setCategoryToDelete(categoryId);
    setIsDeleteConfirmOpen(true);
    handleMenuClose();
  };

  const handleConfirmDelete = async () => {
    if (!isAuthenticated) {
      toast.error('Authentication required to delete categories');
      return;
    }
    if (categoryToDelete) {
      await apiDeleteCategory(categoryToDelete);
      setIsDeleteConfirmOpen(false);
      setCategoryToDelete(null);
      refetchCategories();
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteConfirmOpen(false);
    setCategoryToDelete(null);
  };

  const handleCategoriesReorder = (reordered: Category[]) => {
      setLocalCategories(reordered);
      apiReorderCategories(reordered);
  };

  const handleExperienceEdit = (
    experienceId: string,
    payload?: { name?: string; description?: string; iconURL?: string; locale?: import('../../types/category.types').OccasionLocale }
  ) => {
    if (!payload) return;
    setSelectedExperience({
      id: experienceId,
      title: payload.name || '',
      description: payload.description || '',
      imageUrl: payload.iconURL,
      locale: payload.locale,
    });
    setIsEditExperienceModalOpen(true);
  };

  const handleExperienceChange = () => {
    setExperienceRefresh((prev) => prev + 1);
    setIsEditExperienceModalOpen(false);
    setSelectedExperience(null);
  };

  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Create a debounced version of the search handler
  const debouncedChangeHandler = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearch(value);
      }, 400), // waits 400ms after typing stops
    []
  );

  // This fires immediately on every keystroke but updates the debounced value later
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchQuery(value);
      debouncedChangeHandler(value);
    },
    [debouncedChangeHandler]
  );

  return (
    <Box
      className="page-container"
      sx={{
        padding: {
          xs: '24px 16px',
          sm: '40px 40px',
          md: '40px 80px',
        },
        gap: '24px',
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Page Header */}
      <PageHeader
        title="Categories and Experiences"
        showSearch
        searchPlaceholder={activeTab === 'categories' ? 'Search categories' : 'Search experiences'}
        searchValue={searchQuery}
        onSearchChange={handleSearchChange}
        primaryButton={false}
        actionButtonText={activeTab === 'categories' ? 'New category' : 'New experience'}
        onActionClick={() => {
          if (!isAuthenticated) {
            toast.error(`Authentication required to add ${activeTab === 'categories' ? 'categories' : 'experiences'}`);
            return;
          }
          if (activeTab === 'categories') {
            setIsEditMode(false);
            setSelectedCategory(null);
            setIsCategoryModalOpen(true);
          } else {
            setIsAddExperienceModalOpen(true);
          }
        }}
      />


      {/* Category Modal (Add/Edit) */}
      <Modal
        open={isCategoryModalOpen}
        onClose={() => {
          setIsCategoryModalOpen(false);
          setSelectedCategory(null);
          setIsEditMode(false);
        }}
        title={isEditMode ? "Edit category" : "New category"}
        width={508}
      >
        {isEditMode ? (
          <EditCategoryForm
            categoryId={selectedCategory?.id || ''}
            initialName={selectedCategory?.name || ''}
            initialIconURL={selectedCategory?.image}
            initialSubCategories={selectedCategory?.subCategories || []}
            initialLocale={selectedCategory?.locale}
            onSuccess={() => {
              setIsCategoryModalOpen(false);
              setSelectedCategory(null);
              setIsEditMode(false);
              refetchCategories(); // Ensure list is refreshed
            }}
            onCancel={() => {
              setIsCategoryModalOpen(false);
              setSelectedCategory(null);
              setIsEditMode(false);
            }}
          />
        ) : (
          <AddCategoryForm
            onSuccess={() => {
              setIsCategoryModalOpen(false);
              setSelectedCategory(null);
              setIsEditMode(false);
              refetchCategories(); // Ensure list is refreshed
            }}
            onCancel={() => {
              setIsCategoryModalOpen(false);
              setSelectedCategory(null);
              setIsEditMode(false);
            }}
          />
        )}
      </Modal>

      {/* Tabs and Table Section */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignSelf: 'stretch', gap: '32px', width: '100%', flex: 1, overflow: 'hidden' }}>
        {/* Tabs */}
        <Tabs
          tabs={[
            { key: 'categories', label: 'Categories' },
            { key: 'experiences', label: 'Experiences' },
          ]}
          activeTab={activeTab}
          onTabChange={(tabKey) => setActiveTab(tabKey as typeof activeTab)}
        />

        {/* Tab Content */}
        {activeTab === 'categories' ? (
          loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <CircularProgress sx={{ color: 'var(--palette-light-brand-primary-value-hex)' }} />
            </Box>
          ) : (
            <CategoriesTable
              categories={localCategories.filter(category => {
                if (!debouncedSearch) return true;
                return (category.name || '').toLowerCase().includes(debouncedSearch.toLowerCase());
              })}
              onReorder={handleCategoriesReorder}
              onMenuOpen={handleMenuOpen}
              onCategoryClick={(cat) => navigate(`/categories/${cat.id}`)}
            />
          )
        ) : (
          <ExperiencesTable
            searchQuery={debouncedSearch}
            refreshTrigger={experienceRefresh}
            onExperienceDeleted={() => setExperienceRefresh((prev) => prev + 1)}
            onEditExperience={handleExperienceEdit}
          />
        )}
      </Box>

      {/* Actions Menu */}
      <Dropdown
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        options={[
          {
            label: 'Edit',
            value: 'edit',
            onClick: () => {
            if (selectedItem && activeTab === 'categories') {
                const category = localCategories.find(c => c.id === selectedItem);
                if (category) {
                  handleEditCategory(category);
                }
            }
            handleMenuClose();
            },
          },
          {
            label: 'Delete',
            value: 'delete',
            color: 'danger',
            onClick: () => {
              if (selectedItem && activeTab === 'categories') {
                handleDeleteClick(selectedItem);
              } else {
                handleMenuClose();
              }
            },
          },
        ]}
      />

      {/* Add Experience Modal */}
      <Modal
        open={isAddExperienceModalOpen}
        onClose={() => setIsAddExperienceModalOpen(false)}
        title="Add experience"
        width={508}
      >
        <AddExperienceForm
          onSuccess={() => {
            setIsAddExperienceModalOpen(false);
            setExperienceRefresh((prev) => prev + 1);
          }}
          onCancel={() => setIsAddExperienceModalOpen(false)}
        />
      </Modal>

      {/* Edit Experience Modal */}
      <Modal
        open={isEditExperienceModalOpen}
        onClose={() => {
          setIsEditExperienceModalOpen(false);
          setSelectedExperience(null);
        }}
        title="Edit experience"
        width={508}
      >
        {selectedExperience && (
          <EditExperienceForm
            occasionId={selectedExperience.id}
            initialTitle={selectedExperience.title}
            initialDescription={selectedExperience.description}
            initialImageUrl={selectedExperience.imageUrl}
            initialLocale={selectedExperience.locale}
            onSuccess={handleExperienceChange}
            onCancel={() => {
              setIsEditExperienceModalOpen(false);
              setSelectedExperience(null);
            }}
          />
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={isDeleteConfirmOpen}
        onClose={handleCancelDelete}
        title="Delete Category"
        width={508}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-6-value)' }}>
          <Typography sx={{ 
                    fontSize: 'var(--palette-light-font-size-default-value)',
                    color: 'var(--palette-light-text-primary-value-hex)',
            lineHeight: '24px'
          }}>
            Are you sure you want to delete this category?
            </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-4-value)', marginTop: 'var(--palette-light-4-value)' }}>
            <Button
              primary
              onClick={handleConfirmDelete}
              sx={{
                width: '100%',
                backgroundColor: 'var(--palette-light-status-danger-value-hex)',
                '&:hover': {
                  backgroundColor: '#dc2626',
                }
              }}
            >
              Delete
            </Button>
            <Button
              primary={false}
              bordered={false}
              onClick={handleCancelDelete}
              sx={{ width: '100%' }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default Categories;
